import { useCallback } from 'react';
import Media from 'react-media';

import { Column, Button, useTheme } from '@simplywallst/ui-core';
import ChartUpOutline from '@simplywallst/ui-core/icons/ChartUpOutline';
import SortingOutline from '@simplywallst/ui-core/icons/SortingOutline';
import SidebarOutline from '@simplywallst/ui-core/icons/SidebarOutline';

import { Dropdown } from '@/components/Dropdown';
import type { ButtonRenderProps } from '@/components/Dropdown';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import type { Watchlist } from '@/hooks/useWatchlist';
import {
  WatchlistSorting,
  useWatchlistSorting,
} from '../../../hooks/useWatchlistSorting';
import { useWatchlistPeriod } from '../../../hooks/useWatchlistPeriod';

import HeaderMoreOptions from './HeaderMoreOptions';
import { ChevronDownIcon, DropdownButton, PeriodButton } from './styled';

export interface Props {
  watchlist: Watchlist;
  onToggleTableExpansion: () => void;
}

export const watchlistPeriodLabelsFor = [
  { label: '1M', option: '1M' },
  { label: '3M', option: '3M' },
  { label: '1Y', option: '1Y' },
];

const sortingOptions = [
  {
    option: WatchlistSorting.RECENTLY_ADDED,
    label: 'Recently added',
  },
  {
    option: WatchlistSorting.MARKET_CAP_HIGH_TO_LOW,
    label: 'Market cap high to low',
  },
  {
    option: WatchlistSorting.COMPANY_NAME_A_TO_Z,
    label: 'Company name A - Z',
  },
  {
    option: WatchlistSorting.TICKER_A_TO_Z,
    label: 'Ticker A - Z',
  },
  {
    option: WatchlistSorting.PERFORMANCE_7D,
    label: '7D performance',
  },
  {
    option: WatchlistSorting.DISCOUNT_HIGH_TO_LOW,
    label: 'Most undervalued',
  },
  {
    option: WatchlistSorting.DISCOUNT_LOW_TO_HIGH,
    label: 'Most overvalued',
  },
];

const HeaderActions = ({ watchlist, onToggleTableExpansion }: Props) => {
  const theme = useTheme();
  const { isDesktop } = useUserAgent();

  const [watchlistPeriod, setWatchlistPeriod] = useWatchlistPeriod();
  const [watchlistSorting, setWatchlistSorting] = useWatchlistSorting();

  const periodButton = useCallback(
    ({
      onBlur,
      onFocus,
      onClick,
      innerRef,
      value,
      name,
    }: ButtonRenderProps) => {
      return (
        <PeriodButton
          styleType="passive"
          alignIcon="right"
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          ref={innerRef}
          data-cy-id={name}
        >
          <ChartUpOutline />
          <span>{value}</span>
          <ChevronDownIcon />
        </PeriodButton>
      );
    },
    []
  );

  const sortingButton = useCallback(
    ({ onBlur, onFocus, onClick, innerRef, name }: ButtonRenderProps) => {
      return (
        <DropdownButton
          styleType="passive"
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          ref={innerRef}
          data-cy-id={name}
        >
          <SortingOutline />
        </DropdownButton>
      );
    },
    []
  );

  return (
    <Column
      displayFlex
      alignItem="center"
      width="auto"
      data-cy-id="watchlist-header-buttons"
    >
      <Dropdown
        name="period-selection"
        align="right"
        displayType="inline-block"
        options={watchlistPeriodLabelsFor}
        buttonRender={periodButton}
        value={watchlistPeriod}
        onSelect={(value) => {
          setWatchlistPeriod(value as typeof watchlistPeriod);
        }}
      />
      <Dropdown
        name="sorting-selection"
        align="right"
        displayType="inline-block"
        options={sortingOptions}
        buttonRender={sortingButton}
        value={watchlistSorting}
        onSelect={(value) => {
          setWatchlistSorting(value as WatchlistSorting);
        }}
      />
      <Media query={{ minWidth: theme.lgMinRaw }} defaultMatches={isDesktop}>
        <Button styleType="passive">
          <SidebarOutline
            aria-label="Expand Watchlist Table"
            onClick={onToggleTableExpansion}
          />
        </Button>
      </Media>
      <HeaderMoreOptions watchlist={watchlist} />
    </Column>
  );
};

export default HeaderActions;
